import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import { IndicativeBadge } from "@/components/IndicativeBadge";
import { EUReferencePrices } from "@/components/EUReferencePrices";
import { EUDairyPrices } from "@/components/EUDairyPrices";
import { SharePricesButton } from "@/components/SharePricesButton";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import {
  CaretUp,
  CaretDown,
  MapPin,
  Factory,
  Cow,
  SortAscending,
  SortDescending,
  Funnel,
  ArrowsDownUp,
  Trophy,
  TrendUp,
  TrendDown
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function IrishMarketsPage() {
  const [prices, setPrices] = useState([]);
  const [stats, setStats] = useState(null);
  const [bestPrices, setBestPrices] = useState([]);
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCounty, setSelectedCounty] = useState("");
  const [sortBy, setSortBy] = useState("price_desc");
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pricesRes, countiesRes, bestRes] = await Promise.all([
        axios.get(`${API}/irish-markets/factory-prices`, {
          params: {
            county: selectedCounty || undefined,
            sort_by: sortBy
          }
        }),
        axios.get(`${API}/irish-markets/counties`),
        axios.get(`${API}/irish-markets/best-prices`)
      ]);
      
      setPrices(pricesRes.data.prices);
      setStats(pricesRes.data.stats);
      setLastUpdated(pricesRes.data.last_updated);
      setCounties(countiesRes.data.counties);
      setBestPrices(bestRes.data.best_by_county);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedCounty, sortBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatPrice = (cents) => {
    if (!cents) return "N/A";
    return `€${(cents / 100).toFixed(2)}/kg`;
  };

  const formatCentsPrice = (cents) => {
    if (!cents) return "N/A";
    return `${cents.toFixed(2)}c/kg`;
  };

  const getPriceColor = (price, highest, lowest) => {
    if (!price) return "text-[#6B6661]";
    const range = highest - lowest;
    const position = (price - lowest) / range;
    if (position >= 0.8) return "text-[#059669]";
    if (position >= 0.5) return "text-[#0F766E]";
    if (position >= 0.3) return "text-[#D97706]";
    return "text-[#DC2626]";
  };

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEO title="Irish Livestock Market Prices" description="Live Irish cattle and sheep prices from factories and marts. Compare beef, lamb and dairy prices across Ireland, updated daily." url="/irish-markets" />
      <Header />
      
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider">
              Ireland
            </span>
            <span className="px-2 py-1 bg-[#E5E7EB] text-[#6B6661] text-xs font-mono uppercase tracking-wider">
              Live Data
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#33302E] mb-2">
            Irish Factory Prices
          </h1>
          <div className="mb-2"><IndicativeBadge /></div>
          <p className="text-[#6B6661] font-sans">
            Compare beef prices across Irish meat factories. Find the best place to sell your cattle.
          </p>
          {lastUpdated && (
            <p className="text-xs text-[#6B6661] font-mono mt-2">
              Last updated: {new Date(lastUpdated).toLocaleString("en-IE")}
            </p>
          )}
        </div>

        {/* Official EU Reference Prices */}
        <div className="mb-3"><SharePricesButton /></div>
        <EUReferencePrices />

        {/* Official EU Dairy Quotes */}
        <EUDairyPrices />

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-[#E0D4C8] p-4">
              <div className="flex items-center gap-2 mb-1">
                <Trophy size={18} className="text-[#D97706]" />
                <span className="text-xs font-mono uppercase tracking-wider text-[#6B6661]">Highest</span>
              </div>
              <p className="font-mono text-2xl text-[#059669]">{formatCentsPrice(stats.highest)}</p>
            </div>
            <div className="bg-white border border-[#E0D4C8] p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendUp size={18} className="text-[#0F766E]" />
                <span className="text-xs font-mono uppercase tracking-wider text-[#6B6661]">Average</span>
              </div>
              <p className="font-mono text-2xl text-[#33302E]">{formatCentsPrice(stats.average)}</p>
            </div>
            <div className="bg-white border border-[#E0D4C8] p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendDown size={18} className="text-[#DC2626]" />
                <span className="text-xs font-mono uppercase tracking-wider text-[#6B6661]">Lowest</span>
              </div>
              <p className="font-mono text-2xl text-[#DC2626]">{formatCentsPrice(stats.lowest)}</p>
            </div>
            <div className="bg-white border border-[#E0D4C8] p-4">
              <div className="flex items-center gap-2 mb-1">
                <Factory size={18} className="text-[#0F766E]" />
                <span className="text-xs font-mono uppercase tracking-wider text-[#6B6661]">Factories</span>
              </div>
              <p className="font-mono text-2xl text-[#33302E]">{stats.count}</p>
            </div>
          </div>
        )}

        {/* Best Prices by County - Quick View */}
        <div className="mb-8">
          <h2 className="font-serif text-xl text-[#33302E] mb-4 flex items-center gap-2">
            <Trophy size={20} className="text-[#D97706]" />
            Best Prices by County
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {bestPrices.slice(0, 12).map((price, index) => (
              <div
                key={price.county}
                className={`bg-white border p-3 ${
                  index === 0 ? "border-[#D97706]" : "border-[#E0D4C8]"
                }`}
              >
                {index === 0 && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-[#D97706]">Best Price</span>
                )}
                <p className="font-sans text-sm font-medium text-[#33302E]">{price.county}</p>
                <p className="font-mono text-lg text-[#059669]">{formatCentsPrice(price.price_cents_kg)}</p>
                <p className="text-[10px] text-[#6B6661] truncate">{price.factory_name}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white border border-[#E0D4C8]">
          <div className="flex items-center gap-2">
            <MapPin size={18} className="text-[#0F766E]" />
            <select
              value={selectedCounty}
              onChange={(e) => setSelectedCounty(e.target.value)}
              data-testid="county-filter"
              className="px-3 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
            >
              <option value="">All Counties</option>
              {counties.map((county) => (
                <option key={county.name} value={county.name}>{`${county.name} (${county.factory_count})`}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <ArrowsDownUp size={18} className="text-[#0F766E]" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="sort-filter"
              className="px-3 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
            >
              <option value="price_desc">Highest Price First</option>
              <option value="price_asc">Lowest Price First</option>
              <option value="factory">Factory Name (A-Z)</option>
              <option value="county">County (A-Z)</option>
            </select>
          </div>
        </div>

        {/* Factory Prices Table */}
        <div className="bg-white border border-[#E0D4C8] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F6E9DD]">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                    Rank
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                    Factory
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                    County
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                    Grade
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                    Price (c/kg)
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                    Price (€/kg)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {loading ? (
                  [...Array(10)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3"><div className="h-4 bg-[#E5E7EB] w-8"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-[#E5E7EB] w-40"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-[#E5E7EB] w-24"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-[#E5E7EB] w-16"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-[#E5E7EB] w-20 ml-auto"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-[#E5E7EB] w-20 ml-auto"></div></td>
                    </tr>
                  ))
                ) : (
                  prices.map((price, index) => (
                    <tr key={price.id || index} className="hover:bg-[#F9FAFB]">
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center justify-center w-7 h-7 text-sm font-mono ${
                          index === 0 ? "bg-[#D97706] text-white" : 
                          index === 1 ? "bg-[#9CA3AF] text-white" :
                          index === 2 ? "bg-[#B45309] text-white" :
                          "bg-[#E5E7EB] text-[#6B6661]"
                        }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Factory size={16} className="text-[#6B6661]" />
                          <span className="font-sans text-sm text-[#33302E]">{price.factory_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <MapPin size={14} className="text-[#0F766E]" />
                          <span className="font-sans text-sm text-[#6B6661]">{price.county || "—"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-[#E5E7EB] text-[#6B6661] text-xs font-mono">
                          {price.conformation} / {price.fat_score}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-mono text-lg ${getPriceColor(price.price_cents_kg, stats?.highest, stats?.lowest)}`}>
                          {price.price_cents_kg ? `${price.price_cents_kg.toFixed(2)}` : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono text-sm text-[#6B6661]">
                          {formatPrice(price.price_cents_kg)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 p-6 bg-white border border-[#E0D4C8]">
          <h3 className="font-serif text-lg text-[#33302E] mb-3">About Factory Prices</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-[#6B6661]">
            <div>
              <h4 className="font-sans font-medium text-[#33302E] mb-2">Conformation Grades</h4>
              <p>E (Excellent) → U (Very Good) → R (Good) → O (Fair) → P (Poor)</p>
              <p className="mt-1">+ better than average, = average, - below average</p>
            </div>
            <div>
              <h4 className="font-sans font-medium text-[#33302E] mb-2">Fat Scores</h4>
              <p>1 (Very Lean) → 2 (Lean) → 3 (Average) → 4 (Fat) → 5 (Very Fat)</p>
              <p className="mt-1">Most factories prefer 2= to 3= fat scores</p>
            </div>
          </div>
          <p className="mt-4 text-xs text-[#8B8580]">
            Data sourced from AgriLand.ie. Prices shown are for Heifers R= 3- grade. 
            Actual prices may vary based on individual animal quality, volume, and negotiations.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
